import type { RunCapable } from "./contracts.js";
import { subscribeToRun } from "./events.js";
import type { AgentEvent, CreateRunInput, RunView, Scope } from "./types.js";

/**
 * 実行して完了まで待つ（FR-4.1 / AC-3）。
 *
 * ### なぜ最後に状態を取り直すか（Q3）
 *
 * SSE だけに頼ると、**接続が最後まで持たなかったときに結果が分からない**。
 * Run 自体は成功しているのに、購読が切れただけで失敗扱いにするのは誤り。
 *
 * そこで購読が終わったら必ず `GET /v1/runs/{id}` で最終状態を確定させる。
 * 1 リクエストなので常に行っても安く、**SSE が一切使えない環境でも
 * `run()` は正しく完結する**（進捗が取れないだけ）。
 */

export interface RunOptions {
  readonly scope?: Scope;
  readonly sessionId?: string | null;
  /** 添付の `uploadId`。`upload()` の戻り値から取る。 */
  readonly uploads?: readonly string[];
  readonly workspace?: CreateRunInput["workspace"];
  readonly environmentOverride?: Record<string, unknown>;
  /** 名指しで選ぶ Environment。上位のスコープは重なったまま。 */
  readonly environmentId?: string;
  readonly timeoutSec?: number;

  readonly onEvent?: (event: AgentEvent) => void;
  readonly signal?: AbortSignal;
  /** テストで待たないための差し替え口。 */
  readonly sleep?: (ms: number) => Promise<void>;
  /** 購読を使わず、作成だけして最終状態を取りに行く。 */
  readonly withoutEvents?: boolean;
}

export interface RunResult {
  readonly run: RunView;
  readonly events: readonly AgentEvent[];
  /** 最終メッセージ（あれば）。 */
  readonly finalText: string | undefined;
}

/** 冪等キー。これがあると HTTP 層が安全に再試行できる（Q6）。 */
function newIdempotencyKey(): string {
  return `run-${crypto.randomUUID()}`;
}

/** 添付があるときだけ workspace を組み立てる（セッションの既定を壊さない）。 */
function workspaceFor(options: RunOptions): CreateRunInput["workspace"] | undefined {
  if (options.workspace !== undefined) return options.workspace;
  if (options.uploads === undefined || options.uploads.length === 0) return undefined;
  return {
    source: options.sessionId === undefined || options.sessionId === null ? "empty" : "session",
    uploads: options.uploads,
  };
}

export async function runAndWait(
  client: RunCapable,
  input: { prompt: string } & RunOptions,
): Promise<RunResult> {
  // スコープ未指定なら、自分のテナントを使う（利用側に書かせない）。
  const scope: Scope =
    input.scope ?? { tenantId: (await client.auth.me()).tenantId };
  const workspace = workspaceFor(input);

  const created = await client.runs.create({
    scope,
    prompt: input.prompt,
    idempotencyKey: newIdempotencyKey(),
    ...(input.sessionId === undefined ? {} : { sessionId: input.sessionId }),
    ...(workspace === undefined ? {} : { workspace }),
    ...(input.environmentOverride === undefined
      ? {}
      : { environmentOverride: input.environmentOverride }),
    ...(input.environmentId === undefined ? {} : { environmentId: input.environmentId }),
    ...(input.timeoutSec === undefined ? {} : { timeoutSec: input.timeoutSec }),
  });

  const events: AgentEvent[] = [];

  if (input.withoutEvents !== true) {
    await subscribeToRun(client, created.runId, {
      onEvent: (event) => {
        events.push(event);
        input.onEvent?.(event);
      },
      ...(input.signal === undefined ? {} : { signal: input.signal }),
      ...(input.sleep === undefined ? {} : { sleep: input.sleep }),
    });
  }

  // 中断されたなら Run 自体も止める（放置しない）。
  if (input.signal?.aborted === true) {
    await client.runs.cancel(created.runId).catch(() => undefined);
  }

  // ★ 最終状態は必ずここで確定させる（購読の成否に依存させない）。
  const run = await client.runs.get(created.runId);

  const result = events.find((event) => event.type === "run.result");
  const finalText = (result?.data as { finalText?: string } | undefined)?.finalText;

  return { run, events, finalText };
}
